import { useState } from 'react'
import { Box, Text, Group, Stack, UnstyledButton, Collapse, Badge } from '@mantine/core'
import { IconChevronDown } from '@tabler/icons-react'
import { StatusMeter } from '../common/StatusMeter'
import { EvidenceQuote } from './EvidenceQuote'

/* ─── Bloom's Taxonomy Horizontal Bar ─── */

const bloomLevels = [
  { key: 'remember', label: 'Remember', color: '#4a6fa5' },
  { key: 'understand', label: 'Understand', color: '#2d8f83' },
  { key: 'apply', label: 'Apply', color: '#3aaf5c' },
  { key: 'analyze', label: 'Analyze', color: '#e6a817' },
  { key: 'evaluate', label: 'Evaluate', color: '#e07b39' },
  { key: 'create', label: 'Create', color: '#00e5ff' },
]

function BloomsBar({ distribution = {} }) {
  const total = Object.values(distribution).reduce((s, v) => s + v, 0) || 1

  return (
    <Stack gap="xs">
      {/* Stacked bar */}
      <Box
        style={{
          display: 'flex',
          height: 24,
          borderRadius: 4,
          overflow: 'hidden',
          background: 'rgba(255, 255, 255, 0.04)',
        }}
      >
        {bloomLevels.map(({ key, color }) => {
          const pct = ((distribution[key] || 0) / total) * 100
          if (pct === 0) return null
          return (
            <Box
              key={key}
              style={{
                width: `${pct}%`,
                background: color,
                opacity: 0.75,
                transition: 'width 0.5s ease',
                minWidth: pct > 0 ? 2 : 0,
              }}
            />
          )
        })}
      </Box>
      {/* Legend */}
      <Group gap="xs" wrap="wrap">
        {bloomLevels.map(({ key, label, color }) => {
          const val = distribution[key] || 0
          if (val === 0) return null
          return (
            <Group key={key} gap={4}>
              <Box style={{ width: 8, height: 8, borderRadius: 2, background: color }} />
              <Text size="xs" c="dimmed">
                {label} ({val}%)
              </Text>
            </Group>
          )
        })}
      </Group>
    </Stack>
  )
}

/* ─── Kolb Experiential Cycle ─── */

const kolbStages = [
  { key: 'concrete_experience', label: 'Concrete Experience', pos: 'top' },
  { key: 'reflective_observation', label: 'Reflective Observation', pos: 'right' },
  { key: 'abstract_conceptualization', label: 'Abstract Conceptualization', pos: 'bottom' },
  { key: 'active_experimentation', label: 'Active Experimentation', pos: 'left' },
]

function KolbCycle({ stages_engaged = [] }) {
  return (
    <Box
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gridTemplateRows: '1fr 1fr',
        gap: 8,
        maxWidth: 340,
      }}
    >
      {kolbStages.map(({ key, label }) => {
        const active = stages_engaged.includes(key)
        return (
          <Box
            key={key}
            style={{
              padding: '10px 12px',
              borderRadius: 8,
              background: active
                ? 'rgba(0, 229, 255, 0.08)'
                : 'rgba(255, 255, 255, 0.02)',
              border: `1px solid ${active ? 'rgba(0, 229, 255, 0.25)' : 'rgba(255, 255, 255, 0.06)'}`,
              textAlign: 'center',
            }}
          >
            <Text size="xs" c={active ? 'cyan.3' : 'dimmed'} fw={600}>
              {active ? '✓ ' : ''}{label.replace(/_/g, ' ')}
            </Text>
          </Box>
        )
      })}
    </Box>
  )
}

/* ─── Generic Examples List ─── */

function ExamplesList({ examples = [] }) {
  if (examples.length === 0) return null
  return (
    <Stack gap={0} mt="xs">
      {examples.map((ex, i) => (
        <EvidenceQuote
          key={i}
          evidence={{
            type: 'text_input',
            player_text: ex.player_evidence,
            analysis: ex.explanation,
            beat_context: ex.level || ex.stage,
          }}
        />
      ))}
    </Stack>
  )
}

/* ─── Accordion Item ─── */

const methodologyMeta = {
  blooms_taxonomy: {
    title: "Bloom's Taxonomy",
    description: 'Cognitive complexity of your responses',
    icon: '🧠',
  },
  kolb_experiential: {
    title: "Kolb's Experiential Learning",
    description: 'Learning cycle stages you engaged',
    icon: '🔄',
  },
  deliberate_practice: {
    title: "Ericsson's Deliberate Practice",
    description: 'Indicators of purposeful skill-building',
    icon: '🎯',
  },
  growth_mindset: {
    title: "Dweck's Growth Mindset",
    description: 'Evidence of growth-oriented thinking',
    icon: '🌱',
  },
  reflective_practice: {
    title: "Schon's Reflective Practice",
    description: 'Depth of metacognitive reflection',
    icon: '🪞',
  },
}

// Detect if data uses the new evidence-string format (story analysis)
// vs. the legacy detailed format (full_analysis)
function isEvidenceFormat(data) {
  return typeof data?.evidence === 'string' || typeof data?.evidence_summary === 'string'
}

function EvidenceDisplay({ data, label }) {
  const value = data.level || data.stage || data.focus || data.indicator || data.depth ||
    data.dominant_level || data.dominant_stage
  const evidence = data.evidence || data.evidence_summary

  return (
    <Stack gap="xs">
      {value && (
        <Badge size="sm" variant="light" color="cyan" tt="capitalize">
          {label}: {String(value).replace(/_/g, ' ')}
        </Badge>
      )}
      {evidence && (
        <Text size="sm" c="gray.4" lh={1.5} fs="italic">
          {evidence}
        </Text>
      )}
    </Stack>
  )
}

function MethodologyItem({ methodKey, data }) {
  const [expanded, setExpanded] = useState(false)
  const meta = methodologyMeta[methodKey] || {
    title: methodKey.replace(/_/g, ' '),
    description: '',
    icon: '📊',
  }

  const useSimpleFormat = isEvidenceFormat(data)

  return (
    <Box
      style={{
        borderRadius: 8,
        background: 'rgba(10, 22, 40, 0.5)',
        border: '1px solid rgba(0, 255, 255, 0.08)',
        overflow: 'hidden',
      }}
    >
      <UnstyledButton
        onClick={() => setExpanded((v) => !v)}
        style={{
          width: '100%',
          padding: '12px 16px',
          transition: 'background 0.15s ease',
        }}
      >
        <Group justify="space-between" wrap="nowrap">
          <Group gap="sm">
            <Text size="lg" style={{ lineHeight: 1 }}>
              {meta.icon}
            </Text>
            <Box>
              <Text size="sm" fw={700} ff="Orbitron, sans-serif" c="cyan.3">
                {meta.title}
              </Text>
              <Text size="xs" c="dimmed">
                {meta.description}
              </Text>
            </Box>
          </Group>
          <IconChevronDown
            size={16}
            color="var(--mantine-color-dimmed)"
            style={{
              transform: expanded ? 'rotate(180deg)' : 'rotate(0)',
              transition: 'transform 0.2s ease',
              flexShrink: 0,
            }}
          />
        </Group>
      </UnstyledButton>

      <Collapse in={expanded}>
        <Box style={{ padding: '0 16px 16px' }}>
          {/* New evidence-string format (story analysis / user profile) */}
          {useSimpleFormat && (
            <EvidenceDisplay data={data} label={meta.title} />
          )}

          {/* Legacy detailed format (full_analysis) */}
          {!useSimpleFormat && (
            <>
              {/* Bloom's — stacked bar + examples */}
              {methodKey === 'blooms_taxonomy' && data && (
                <Stack gap="md">
                  <BloomsBar distribution={data.distribution} />
                  <ExamplesList examples={data.examples} />
                </Stack>
              )}

              {/* Kolb — cycle diagram + examples */}
              {methodKey === 'kolb_experiential' && data && (
                <Stack gap="md">
                  <KolbCycle stages_engaged={data.stages_engaged} />
                  <ExamplesList examples={data.examples} />
                </Stack>
              )}

              {/* Deliberate Practice — indicators + examples */}
              {methodKey === 'deliberate_practice' && data && (
                <Stack gap="md">
                  {data.indicators?.length > 0 && (
                    <Group gap="xs" wrap="wrap">
                      {data.indicators.map((ind, i) => (
                        <Badge key={i} size="sm" variant="light" color="cyan">
                          {ind}
                        </Badge>
                      ))}
                    </Group>
                  )}
                  <ExamplesList examples={data.examples} />
                </Stack>
              )}

              {/* Growth Mindset — evidence + examples */}
              {methodKey === 'growth_mindset' && data && (
                <Stack gap="md">
                  {data.evidence?.length > 0 && (
                    <Group gap="xs" wrap="wrap">
                      {data.evidence.map((ev, i) => (
                        <Badge key={i} size="sm" variant="light" color="green">
                          {ev}
                        </Badge>
                      ))}
                    </Group>
                  )}
                  <ExamplesList examples={data.examples} />
                </Stack>
              )}

              {/* Reflective Practice — depth score + examples */}
              {methodKey === 'reflective_practice' && data && (
                <Stack gap="md">
                  {data.depth_score != null && (
                    <Box>
                      <Text size="xs" c="dimmed" mb={4}>
                        Reflective Depth
                      </Text>
                      <StatusMeter
                        value={data.depth_score}
                        color={data.depth_score >= 70 ? 'cyan' : data.depth_score >= 40 ? 'orange' : 'red'}
                      />
                    </Box>
                  )}
                  <ExamplesList examples={data.examples} />
                </Stack>
              )}
            </>
          )}
        </Box>
      </Collapse>
    </Box>
  )
}

/* ─── Main Component ─── */

const METHODOLOGY_ORDER = [
  'blooms_taxonomy',
  'kolb_experiential',
  'deliberate_practice',
  'growth_mindset',
  'reflective_practice',
]

export function MethodologySection({ insights = {} }) {
  return (
    <Stack gap="sm">
      {METHODOLOGY_ORDER.map((key) => {
        const data = insights[key]
        if (!data) return null
        return <MethodologyItem key={key} methodKey={key} data={data} />
      })}
    </Stack>
  )
}
